import React, { useEffect, useState, useCallback } from 'react';
import { motion } from 'framer-motion';

// ═══════════════════════════════════════════════════════════
//  MiroPanel — knowledge base + AI architect cockpit
//
//  Three sections in one tab:
//    1. Cases gallery — list every architecture we've imported.
//    2. Import — paste a Miro board URL, backend pulls components.
//    3. Architect — describe a project, preview the plan, then
//       materialize on Miro only after the user confirms.
//
//  The "preview before materialize" loop is what makes this
//  feel intentional rather than a slot machine — Mira shows
//  her shape first, you accept, then ink hits the canvas.
// ═══════════════════════════════════════════════════════════

const API = (process.env.REACT_APP_API_URL || 'http://localhost:3001').replace(/\/+$/, '');

interface ArchComponentSummary {
  name: string;
  type: string;
  description: string;
  connections: string[];
}

interface MiroCase {
  id: string;
  name: string;
  projectType: string;
  description: string;
  tags: string[];
  boardUrl?: string;
  components: ArchComponentSummary[];
  createdAt: string;
  /** True for the 4 hardcoded ship-with-the-app examples. UI shows
   *  them with a distinct badge so the operator can tell their own
   *  imports apart from the seed catalogue. */
  isSeed?: boolean;
}

interface MiroBoardLite {
  id: string;
  name: string;
  description: string;
  viewLink?: string;
  modifiedAt?: string;
}

interface PlanComponent {
  name: string;
  category: string;
  description: string;
  layer: string;
  /** Optional Miro shape name (cloud, right_brace, diamond, …) —
   *  the architect picks this from the operator's imported shape
   *  vocabulary so the rendered board mirrors their existing
   *  artifacts. When undefined, the renderer falls back to the
   *  category default. Mirrors backend ArchitectPlanJson. */
  shape?: string;
  /** LATO step number ('[1]', '[3A]', '[5.2]'). Bold-prefixed on
   *  the rendered board. Intake nodes (phone line, web form) may
   *  skip it. */
  step?: string;
  /** 4-8 short bullets describing the step's behaviour, inputs,
   *  validation, decision logic. Rendered as a bullet list inside
   *  the box on the Miro board AND in this preview. */
  criteria?: string[];
}

interface PlanConnector {
  from: string;
  to: string;
  label?: string;
  kind?: 'solid' | 'dashed' | 'dotted';
  /** happy = main downward flow [n]→[n+1] (thicker line);
   *  side = alternative path off a decision (thinner). */
  branch?: 'happy' | 'side';
}

/** LATO-style side-card attached to a component. */
interface PlanAnnotation {
  label: string;        // "API: Twilio (For SMS)" / "Note: TBD"
  attachTo: string;     // exact component name
  side?: 'left' | 'right';
  kind?: 'api' | 'note';
}

interface PlanJson {
  title: string;
  summary: string;
  layout: 'workflow' | 'layered' | 'grid' | 'hub' | 'flow';
  components: PlanComponent[];
  connectors: PlanConnector[];
  /** API + note cards (e.g. "API: Twilio (For SMS)") attached to
   *  specific steps. Rendered as a side panel in the preview. */
  annotations?: PlanAnnotation[];
  /** 5-8 functional bullets summarising what the Centralized
   *  Dashboard delivers (LATO methodology section 11). Rendered
   *  as a footer block below the workflow. */
  keyCapabilities?: string[];
  /** Proposal-ready framing per LATO methodology section 11.1/11.2.
   *  When present, replace title/summary on the navy header band.
   *  phaseTitle reflects scope + main value; phaseDescription is
   *  1-3 sentences (what / why / enables). */
  phaseTitle?: string;
  phaseDescription?: string;
  /** Multi-case structure. Each entry is one case section; the
   *  renderer wraps that case's components in a colored frame on
   *  the Miro board (purple/green/pink/blue cycle). */
  cases?: Array<{
    number: string;
    title: string;
    description?: string;
  }>;
}

interface LayoutResult {
  components: Array<PlanComponent & { x: number; y: number; width: number; height: number; layer: string; elementType: string }>;
  layerLabels: Array<{ name: string; x: number; y: number }>;
  bounds: { minX: number; minY: number; maxX: number; maxY: number };
}

interface MiroStatus {
  connected: boolean;
  reason?: string;
  message?: string;
  tokenPrefix?: string;
  tokenLength?: number;
  teamId?: string | null;
  templateBoardId?: string | null;
  org?: string | null;
  boardsAccessible?: number | null;
  mcpEnabled?: boolean;
}

const PROJECT_TYPES = ['web-app', 'microservices', 'data-pipeline', 'ml-system', 'platform', 'mobile-app', 'integration', 'other'];

export const MiroPanel: React.FC = () => {
  const [cases, setCases] = useState<MiroCase[]>([]);

  // Cases are needed in BOTH sections — gallery shows them, the
  // architect form picks among them as few-shot. Lift state so a
  // freshly imported case appears in the picker without a remount.
  const reloadCases = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/miro/cases`);
      if (r.ok) {
        const data = await r.json();
        if (Array.isArray(data)) setCases(data);
      }
    } catch {}
  }, []);

  useEffect(() => { reloadCases(); }, [reloadCases]);

  return (
    <div className="miro-panel">
      <StatusBar />
      <CasesSection cases={cases} onChanged={reloadCases} />
      <ArchitectSection cases={cases} />
      <ElementsSection />
    </div>
  );
};

// ═══════════════════════════════════════════════════════════
//  Status bar — does Mira have everything she needs?
// ═══════════════════════════════════════════════════════════

const StatusBar: React.FC = () => {
  const [status, setStatus] = useState<MiroStatus | null>(null);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/miro/status`);
      if (r.ok) setStatus(await r.json());
      else setStatus({ connected: false, reason: `http_${r.status}` });
    } catch {
      setStatus({ connected: false, reason: 'network_error' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  if (loading && !status) {
    return <div className="miro-status miro-status-loading">Проверяю Miro…</div>;
  }
  if (!status) return null;

  if (!status.connected) {
    const map: Record<string, string> = {
      token_missing: 'MIRO_TOKEN не задан в env пода. Положи в Vault и сделай `kubectl rollout restart`.',
      token_invalid: 'Токен есть, но Miro его не принял. Возможно отозван — переустанови app в Miro Settings.',
      network_error: 'Не достучаться до api.miro.com. Проверь сеть пода.',
      probe_failed: 'Probe failed — детали в логах пода.',
    };
    return (
      <div className="miro-status miro-status-bad">
        <span className="miro-status-dot" />
        <span className="miro-status-label">Miro disconnected</span>
        <span className="miro-status-reason">
          {map[status.reason || ''] || status.message || status.reason || 'unknown'}
        </span>
        <button className="miro-status-refresh" onClick={reload}>Перепроверить</button>
      </div>
    );
  }

  return (
    <div className="miro-status miro-status-ok">
      <span className="miro-status-dot" />
      <span className="miro-status-label">Miro connected</span>
      <div className="miro-status-fields">
        {status.org && <span><b>org:</b> {status.org}</span>}
        {status.boardsAccessible !== null && status.boardsAccessible !== undefined && (
          <span><b>boards:</b> {status.boardsAccessible}</span>
        )}
        <span><b>token:</b> <code>{status.tokenPrefix}</code></span>
        {status.teamId && <span><b>team:</b> <code>{status.teamId}</code></span>}
        {status.templateBoardId && <span><b>template:</b> <code>{status.templateBoardId}</code></span>}
        {status.mcpEnabled && <span className="miro-status-badge">MCP</span>}
      </div>
      <button className="miro-status-refresh" onClick={reload}>↻</button>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════
//  Cases section — gallery + import-from-URL
// ═══════════════════════════════════════════════════════════

const CasesSection: React.FC<{ cases: MiroCase[]; onChanged: () => void }> = ({ cases, onChanged }) => {
  const [importUrl, setImportUrl] = useState('');
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<string | null>(null);
  const loading = false;

  // Real Miro boards from the user's account — pulled lazily once
  // the operator opens the quick-pick. Lets them import a board
  // by clicking instead of pasting a URL.
  const [boards, setBoards] = useState<MiroBoardLite[] | null>(null);
  const [boardsLoading, setBoardsLoading] = useState(false);
  const [boardsErr, setBoardsErr] = useState<string | null>(null);
  const [showBoards, setShowBoards] = useState(false);

  const loadBoards = async () => {
    setBoardsLoading(true);
    setBoardsErr(null);
    try {
      const r = await fetch(`${API}/api/miro/boards`);
      const data = await r.json();
      if (!r.ok) {
        setBoardsErr(data.message || data.error || `HTTP ${r.status}`);
        setBoards([]);
      } else {
        setBoards(data.boards || []);
      }
    } catch (err: any) {
      setBoardsErr(err.message || 'network error');
      setBoards([]);
    } finally {
      setBoardsLoading(false);
    }
  };

  const togglePicker = () => {
    setShowBoards((prev) => {
      const next = !prev;
      if (next && boards === null) loadBoards();
      return next;
    });
  };

  const submitImport = async (urlOverride?: string) => {
    const url = (urlOverride ?? importUrl).trim();
    if (!url) return;
    setImporting(true);
    setImportResult(null);
    try {
      const r = await fetch(`${API}/api/miro/cases/import`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ boardUrl: url }),
      });
      const data = await r.json();
      if (r.ok) {
        setImportResult(
          `✓ Импортировано: "${data.case.name}" — ${data.componentsFound} компонентов, ${data.connectorsFound} связей`
          + (data.elementsExtracted ? ` · +${data.elementsExtracted} стилей в каталог элементов` : ''),
        );
        setImportUrl('');
        onChanged();
      } else {
        const map: Record<string, string> = {
          invalid_board_url: 'Не удалось распарсить URL. Скопируй полную ссылку из адресной строки Miro.',
          no_components_found: 'На доске не нашлось ни одного компонента (возможно, доска пустая или нет доступа).',
          miro_not_configured: 'MIRO_TOKEN не настроен на сервере.',
        };
        setImportResult(`✗ ${map[data.error] || data.message || data.error || 'Ошибка'}`);
      }
    } catch (err: any) {
      setImportResult(`✗ ${err.message || 'Network error'}`);
    } finally {
      setImporting(false);
    }
  };

  const importByPickedBoard = async (b: MiroBoardLite) => {
    if (!b.viewLink) {
      setImportResult(`✗ У доски "${b.name}" нет viewLink`);
      return;
    }
    await submitImport(b.viewLink);
  };

  // Batch import — POST /api/miro/cases/import-all walks every
  // board the token can see, skips ones already in the catalog,
  // and surfaces a per-board outcome summary. Sequential by
  // design (rate limit), so progress UI is just "идёт…".
  const [batchImporting, setBatchImporting] = useState(false);
  const importAllBoards = async () => {
    if (batchImporting) return;
    if (!window.confirm('Импортировать все доски твоего Miro как кейсы? Уже импортированные пропустятся.')) return;
    setBatchImporting(true);
    setImportResult(null);
    try {
      const r = await fetch(`${API}/api/miro/cases/import-all`, { method: 'POST' });
      const data = await r.json();
      if (!r.ok) {
        setImportResult(`✗ ${data.message || data.error || `HTTP ${r.status}`}`);
        return;
      }
      const parts = [
        `✓ ${data.summary || ''}`,
        data.imported?.length > 0 && `Затащено: ${data.imported.map((x: any) => x.name).join(', ')}`,
        data.skipped?.length > 0 && `Пропущено (уже в базе или без линка): ${data.skipped.length}`,
        data.failures?.length > 0 && `Ошибки: ${data.failures.map((x: any) => `${x.name} — ${x.reason}`).join('; ')}`,
      ].filter(Boolean).join(' · ');
      setImportResult(parts);
      onChanged();
      // Refresh the boards list so already-imported entries
      // get filtered out next time the picker opens.
      if (boards) await loadBoards();
    } catch (err: any) {
      setImportResult(`✗ ${err.message || 'Network error'}`);
    } finally {
      setBatchImporting(false);
    }
  };

  const removeCase = async (id: string) => {
    if (!window.confirm('Удалить кейс из базы?')) return;
    await fetch(`${API}/api/miro/cases/${id}`, { method: 'DELETE' });
    onChanged();
  };

  // Counts for the section header — "user-imported / seed
  // examples" reads as a real catalog rather than "we have 4
  // mystery cards".
  const userCount = cases.filter((c) => !c.isSeed).length;
  const seedCount = cases.filter((c) => c.isSeed).length;

  return (
    <div className="miro-section">
      <div className="miro-section-head">
        <h3>
          База архитектур · {cases.length}
          {userCount > 0 && (
            <span className="miro-section-count-detail">
              {' '}({userCount} {userCount === 1 ? 'твой' : 'твоих'} + {seedCount} примеров)
            </span>
          )}
        </h3>
        <span className="miro-section-hint">Ссылка-эталон → AI учится на твоих досках</span>
      </div>

      <div className="miro-import">
        <input
          type="text"
          className="miro-input"
          placeholder="https://miro.com/app/board/uXjV.../"
          value={importUrl}
          onChange={(e) => setImportUrl(e.target.value)}
          disabled={importing}
        />
        <button
          className="btn-primary miro-import-btn"
          onClick={() => submitImport()}
          disabled={importing || !importUrl.trim()}
        >
          {importing ? 'Импортирую…' : 'Импортировать'}
        </button>
      </div>

      {/* Quick-pick: list user's actual Miro boards. Lazy-loaded
          on first open — no extra API hit until the operator
          actually wants the picker. Click a row → triggers the
          same /import endpoint with the board's viewLink. */}
      <div className="miro-boards-picker">
        <div className="miro-boards-picker-row">
          <button
            className="miro-boards-toggle"
            onClick={togglePicker}
            disabled={importing || batchImporting}
          >
            {showBoards ? '▼ Мои Miro-доски' : '▶ Показать мои Miro-доски'}
            {boards && ` (${boards.length})`}
          </button>
          {/* "Import all" — only renders once boards loaded
              successfully and there's at least one to import.
              Confirm-before-fire so a stray click doesn't spawn
              N background imports. */}
          {boards && boards.length > 0 && (
            <button
              className="miro-boards-import-all"
              onClick={importAllBoards}
              disabled={importing || batchImporting}
              title="Импортировать все доски одним батчем (уже импортированные пропустятся)"
            >
              {batchImporting ? '…' : '⇩ Импортировать все'}
            </button>
          )}
        </div>
        {showBoards && (
          <div className="miro-boards-list">
            {boardsLoading && <div className="miro-empty">Тяну список из Miro…</div>}
            {!boardsLoading && boardsErr && (
              <div className="miro-import-result bad">✗ {boardsErr}</div>
            )}
            {!boardsLoading && !boardsErr && boards && boards.length === 0 && (
              <div className="miro-empty">У токена не видно ни одной доски.</div>
            )}
            {!boardsLoading && !boardsErr && boards && boards.map((b) => (
              <div key={b.id} className="miro-board-row">
                <div className="miro-board-info">
                  <div className="miro-board-name">{b.name}</div>
                  {b.description && <div className="miro-board-desc">{b.description}</div>}
                  {b.modifiedAt && (
                    <div className="miro-board-time">
                      изменено: {new Date(b.modifiedAt).toLocaleString('ru-RU')}
                    </div>
                  )}
                </div>
                <div className="miro-board-actions">
                  {b.viewLink && (
                    <a
                      href={b.viewLink}
                      target="_blank"
                      rel="noreferrer"
                      className="miro-board-view"
                      title="Открыть доску в Miro"
                    >
                      ↗
                    </a>
                  )}
                  <button
                    className="miro-board-import"
                    onClick={() => importByPickedBoard(b)}
                    disabled={importing}
                    title="Импортировать как кейс"
                  >
                    Импорт
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {importResult && (
        <div className={`miro-import-result ${importResult.startsWith('✓') ? 'ok' : 'bad'}`}>
          {importResult}
        </div>
      )}

      {loading && <div className="miro-empty">Загружаю кейсы…</div>}
      {!loading && cases.length === 0 && (
        <div className="miro-empty">
          Пока ни одного кейса — вставь ссылку на свою Miro-доску выше, бэк скачает компоненты и положит сюда.
        </div>
      )}
      {!loading && cases.length > 0 && (
        <>
          {/* Catalog header — splits "yours" from "examples" so
              user-imported cases read as primary content. */}
          {userCount > 0 && seedCount > 0 && (
            <div className="miro-cases-divider">
              <span className="miro-cases-section-label">Твои импорты · {userCount}</span>
            </div>
          )}
          <div className="miro-cases-grid">
            {cases.filter((c) => !c.isSeed).map((c, i) => (
              <CaseCard key={c.id} c={c} i={i} onRemove={removeCase} />
            ))}
          </div>
          {seedCount > 0 && (
            <>
              <div className="miro-cases-divider miro-cases-divider-seed">
                <span className="miro-cases-section-label">Примеры · {seedCount}</span>
                <span className="miro-cases-section-hint">встроенные эталоны для AI</span>
              </div>
              <div className="miro-cases-grid">
                {cases.filter((c) => c.isSeed).map((c, i) => (
                  <CaseCard key={c.id} c={c} i={i} onRemove={removeCase} />
                ))}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

// Single case card — extracted so the section can render two
// grids (user imports + seeds) without duplicating markup.
const CaseCard: React.FC<{
  c: MiroCase;
  i: number;
  onRemove: (id: string) => void;
}> = ({ c, i, onRemove }) => (
  <motion.div
    className={`miro-case-card${c.isSeed ? ' miro-case-card-seed' : ''}`}
    initial={{ opacity: 0, y: 8 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: i * 0.04 }}
  >
    <div className="miro-case-head">
      <div className="miro-case-name">{c.name}</div>
      <div className="miro-case-head-right">
        {c.isSeed
          ? <span className="miro-case-badge miro-case-badge-seed" title="Встроенный пример">пример</span>
          : <span className="miro-case-badge miro-case-badge-import" title="Импортирован из твоего Miro">из Miro</span>}
        <button className="miro-case-remove" onClick={() => onRemove(c.id)} title="Удалить">×</button>
      </div>
    </div>
    <div className="miro-case-type">{c.projectType}</div>
    {c.description && <div className="miro-case-desc">{c.description}</div>}
    <div className="miro-case-meta">
      <span>{c.components.length} компонентов</span>
      <span>·</span>
      <span>
        {c.components.reduce((sum, x) => sum + x.connections.length, 0)} связей
      </span>
      {c.boardUrl && (
        <>
          <span>·</span>
          <a href={c.boardUrl} target="_blank" rel="noreferrer">Доска ↗</a>
        </>
      )}
    </div>
    {c.tags.length > 0 && (
      <div className="miro-case-tags">
        {c.tags.map((t) => <span key={t} className="miro-case-tag">{t}</span>)}
      </div>
    )}
  </motion.div>
);

// ═══════════════════════════════════════════════════════════
//  Architect section — preview → confirm → materialize
// ═══════════════════════════════════════════════════════════

// CLI model aliases the architect's --model flag accepts. Same
// flag the code-runner takes, so behaviour mirrors the rest of
// the system.
const ARCHITECT_MODELS: Array<{ id: string; label: string; hint: string }> = [
  { id: 'opus',   label: 'Medium',     hint: '$0.22 / доска · лучшая структура' },
  { id: 'sonnet', label: 'Sonnet 4.6', hint: '$0.05 / доска · базис, дёшево' },
  { id: 'haiku',  label: 'Haiku 4.5',  hint: '$0.01 / доска · быстро, для черновика' },
];

interface CallTelemetry {
  model: string;
  inputTokens: number;
  cacheReadTokens: number;
  cacheCreationTokens: number;
  outputTokens: number;
  costUsd: number;
  durationMs: number;
}

const ArchitectSection: React.FC<{ cases: MiroCase[] }> = ({ cases }) => {
  const [projectName, setProjectName] = useState('');
  const [description, setDescription] = useState('');
  const [projectType, setProjectType] = useState('web-app');
  const [tagsText, setTagsText] = useState('');
  // Default LATO template board ID — the clean strip Dana maintains
  // (navy header + LATO logo + workflow frame + Symbol Legend). The
  // backend personalises it on materialize (swaps [Client Name],
  // removes Phase 1 / Name / Description placeholders). Operator can
  // override per-generation by typing a different ID below.
  const DEFAULT_TEMPLATE_BOARD_ID = 'uXjVHNyLFR0=';
  const [templateBoardId, setTemplateBoardId] = useState(DEFAULT_TEMPLATE_BOARD_ID);
  const [selectedCaseIds, setSelectedCaseIds] = useState<string[]>([]);
  // Default Opus — for one-shot architecture reasoning the
  // marginal $0.17 over Sonnet pays for itself in fewer retries
  // and sharper component selection.
  const [model, setModel] = useState<string>('opus');

  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState<{ plan: PlanJson; layout: LayoutResult; casesUsed: Array<{ id: string; name: string }>; telemetry: CallTelemetry | null } | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [materializing, setMaterializing] = useState(false);
  const [boardResult, setBoardResult] = useState<{ url: string; components: number; connectors: number; telemetry: CallTelemetry | null } | null>(null);

  const canPreview = projectName.trim().length >= 3 && description.trim().length >= 20;

  const toggleCase = (id: string) => {
    setSelectedCaseIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : prev.length >= 5 ? prev : [...prev, id],
    );
  };

  // refineNote — populated by the operator AFTER seeing a preview
  // they want changed. Passed to the next /preview call so the
  // architect honours the correction. Kept across re-previews so
  // the field stays visible with what was asked.
  const [refineNote, setRefineNote] = useState('');

  // runPreview doubles as "re-preview with a correction". When
  // `refine` is passed it goes into the request body; otherwise a
  // clean first-pass preview.
  const runPreview = async (refine?: string) => {
    if (!canPreview) return;
    setPreviewing(true);
    setPreview(null);
    setPreviewError(null);
    setBoardResult(null);
    try {
      const r = await fetch(`${API}/api/miro/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          projectName: projectName.trim(),
          projectDescription: description.trim(),
          projectType,
          tags: tagsText.split(',').map((t) => t.trim()).filter(Boolean),
          templateBoardId: templateBoardId.trim() || undefined,
          caseIds: selectedCaseIds.length ? selectedCaseIds : undefined,
          model,
          refineNote: refine && refine.trim() ? refine.trim() : undefined,
        }),
      });
      const data = await r.json();
      if (r.ok) setPreview({ plan: data.plan, layout: data.layout, casesUsed: data.casesUsed || [], telemetry: data.telemetry || null });
      else setPreviewError(data.message || data.error || 'preview_failed');
    } catch (err: any) {
      setPreviewError(err.message || 'Network error');
    } finally {
      setPreviewing(false);
    }
  };

  const runMaterialize = async () => {
    if (!preview) return;
    setMaterializing(true);
    try {
      const r = await fetch(`${API}/api/miro/materialize`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          plan: preview.plan,
          layout: preview.layout,
          projectName: projectName.trim(),
          templateBoardId: templateBoardId.trim() || undefined,
        }),
      });
      const data = await r.json();
      if (r.ok) {
        setBoardResult({
          url: data.boardUrl,
          components: data.componentsCreated,
          connectors: data.connectorsCreated,
          telemetry: data.telemetry || null,
        });
      } else {
        setPreviewError(data.message || data.error || 'materialize_failed');
      }
    } catch (err: any) {
      setPreviewError(err.message || 'Network error');
    } finally {
      setMaterializing(false);
    }
  };

  return (
    <div className="miro-section">
      <div className="miro-section-head">
        <h3>Mira рисует</h3>
        <span className="miro-section-hint">Опиши проект → preview → подтвердить → доска в Miro</span>
      </div>

      <label className="form-label">Название проекта <span className="form-req">*</span></label>
      <input className="form-input" placeholder="Lato AI Dev Team" value={projectName} onChange={(e) => setProjectName(e.target.value)} />

      <label className="form-label" style={{ marginTop: 14 }}>
        Описание <span className="form-req">*</span>
        <span className="form-hint"> — чем подробнее, тем лучше</span>
      </label>
      <textarea
        className="form-textarea"
        rows={5}
        placeholder="Что делает, какие сервисы, какие данные, с чем интегрируется… или загрузи аудио / .txt ниже"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
      />
      <AudioDropzone onSummary={(text) => setDescription(text)} />

      <label className="form-label" style={{ marginTop: 14 }}>Тип проекта</label>
      <div className="form-chips">
        {PROJECT_TYPES.map((t) => (
          <button
            key={t}
            className={`form-chip ${projectType === t ? 'form-chip-active' : ''}`}
            onClick={() => setProjectType(t)}
            type="button"
          >{t}</button>
        ))}
      </div>

      <label className="form-label" style={{ marginTop: 14 }}>Tech tags (через запятую)</label>
      <input
        className="form-input"
        placeholder="react, postgres, kafka, k8s"
        value={tagsText}
        onChange={(e) => setTagsText(e.target.value)}
      />

      {/* Model picker — Opus default for sharper structural
          reasoning, Sonnet/Haiku as cheaper alternatives. Hint
          shows ballpark cost so the operator picks consciously. */}
      <label className="form-label" style={{ marginTop: 14 }}>Модель архитектора</label>
      <div className="form-chips">
        {ARCHITECT_MODELS.map((m) => (
          <button
            key={m.id}
            className={`form-chip ${model === m.id ? 'form-chip-active' : ''}`}
            onClick={() => setModel(m.id)}
            type="button"
            title={m.hint}
          >
            {m.label}
          </button>
        ))}
      </div>
      <div className="miro-model-hint">
        {ARCHITECT_MODELS.find((m) => m.id === model)?.hint}
      </div>

      {cases.length > 0 && (
        <>
          <label className="form-label" style={{ marginTop: 14 }}>
            Эталонные кейсы из базы
            <span className="form-hint"> — оставь пусто чтобы Mira подобрала автоматически</span>
          </label>
          <div className="miro-case-picker">
            {cases.map((c) => {
              const active = selectedCaseIds.includes(c.id);
              return (
                <button
                  key={c.id}
                  type="button"
                  className={`miro-case-chip ${active ? 'miro-case-chip-active' : ''}`}
                  onClick={() => toggleCase(c.id)}
                  title={`${c.projectType} · ${c.components.length} компонентов`}
                  disabled={!active && selectedCaseIds.length >= 5}
                >
                  {active ? '✓ ' : ''}{c.name}
                </button>
              );
            })}
          </div>
          {selectedCaseIds.length > 0 && (
            <div className="miro-case-picker-meta">
              {selectedCaseIds.length} / 5 выбрано — эти кейсы пойдут в few-shot
            </div>
          )}
        </>
      )}

      <details className="form-advanced">
        <summary>Advanced: template board ID</summary>
        <input
          className="form-input"
          style={{ marginTop: 10 }}
          placeholder={DEFAULT_TEMPLATE_BOARD_ID}
          value={templateBoardId}
          onChange={(e) => setTemplateBoardId(e.target.value)}
        />
        <div style={{ marginTop: 6, fontSize: 11, color: '#7a7590' }}>
          Default points at Dana's clean LATO template — gets duplicated and
          personalised per project (swaps [Client Name], strips Phase 1 / Name
          / Description placeholders). Override only if you have a different
          empty template.
        </div>
      </details>

      <div className="form-actions">
        {previewError && <span className="form-result bad">✗ {previewError}</span>}
        {!preview && (
          <button className="btn-primary" onClick={() => runPreview()} disabled={!canPreview || previewing}>
            {previewing ? 'Думаю…' : 'Сгенерировать план'}
          </button>
        )}
      </div>

      {/* ─── Preview ─── */}
      {preview && !boardResult && (
        <div className="miro-preview">
          <div className="miro-preview-head">
            <div>
              {/* LATO methodology 11.1/11.2 — phaseTitle/phaseDescription
                  override title/summary on the header band when present.
                  We mirror that here so the preview shows what'll
                  actually land on the navy header. */}
              <div className="miro-preview-title">{preview.plan.phaseTitle || preview.plan.title}</div>
              <div className="miro-preview-summary">{preview.plan.phaseDescription || preview.plan.summary}</div>
              <div className="miro-preview-layout">layout: <b>{preview.plan.layout}</b> · {preview.plan.components.length} компонентов · {preview.plan.connectors.length} связей</div>
              {preview.casesUsed && preview.casesUsed.length > 0 && (
                <div className="miro-preview-cases">
                  <span className="miro-preview-cases-label">few-shot:</span>
                  {preview.casesUsed.map((c) => (
                    <span key={c.id} className="miro-preview-case">{c.name}</span>
                  ))}
                </div>
              )}
              {/* Token + cost breakdown for THIS preview call.
                  Mirrors the History drawer's cost block — same
                  numbers, same shape. Only renders when telemetry
                  came back (older backend won't include it). */}
              {preview.telemetry && <ArchitectCostStrip t={preview.telemetry} />}
            </div>
            <div className="miro-preview-actions">
              <button className="btn-secondary" onClick={() => { setPreview(null); setPreviewError(null); }} disabled={materializing}>
                ← Изменить
              </button>
              <button className="btn-primary" onClick={runMaterialize} disabled={materializing}>
                {materializing ? 'Рисую на Miro…' : 'Подтвердить и нарисовать ↗'}
              </button>
            </div>
          </div>

          <PlanPreviewView plan={preview.plan} />

          {/* Refinement loop — operator reviewed the plan above and
              wants changes. The note goes into the next /preview
              call as a HIGH-PRIORITY correction. Cheaper than a
              from-scratch redo: the cached system block is reused,
              only the (small) user prompt changes. */}
          <div className="miro-refine">
            <label className="miro-refine-label">Что поправить в плане</label>
            <textarea
              className="form-textarea"
              rows={3}
              placeholder="напр.: CRM это hub — все потоки веди через него; вебформа кормит email-инбокс, не отдельный канал"
              value={refineNote}
              onChange={(e) => setRefineNote(e.target.value)}
              disabled={previewing}
            />
            <button
              className="btn-secondary miro-refine-btn"
              onClick={() => runPreview(refineNote)}
              disabled={previewing || !refineNote.trim()}
            >
              {previewing ? 'Перерисовываю…' : '↻ Перегенерировать с правками'}
            </button>
          </div>
        </div>
      )}

      {/* ─── Materialized ─── */}
      {boardResult && (
        <div className="miro-result">
          <div className="miro-result-head">✓ Доска готова</div>
          <a className="miro-result-link" href={boardResult.url} target="_blank" rel="noreferrer">
            {boardResult.url} ↗
          </a>
          <div className="miro-result-meta">
            {boardResult.components} компонентов · {boardResult.connectors} связей
          </div>
          {boardResult.telemetry && <ArchitectCostStrip t={boardResult.telemetry} />}
          <button
            className="btn-secondary"
            style={{ marginTop: 14 }}
            onClick={() => { setPreview(null); setBoardResult(null); setProjectName(''); setDescription(''); setTagsText(''); }}
          >
            Новая архитектура
          </button>
        </div>
      )}
    </div>
  );
};

// Per-call telemetry strip — shown after preview AND after
// materialize. Same shape both times so the operator gets
// consistent cost feedback. Renders nothing when CLI didn't
// report usage (very old responses, fallback paths).
const ArchitectCostStrip: React.FC<{ t: CallTelemetry }> = ({ t }) => {
  if (!t || (t.costUsd === 0 && t.inputTokens === 0)) return null;
  const cachePct = t.inputTokens > 0
    ? Math.round((t.cacheReadTokens / t.inputTokens) * 100)
    : 0;
  const fmt = (n: number) =>
    n >= 1 ? `$${n.toFixed(2)}`
    : n > 0 ? `$${n.toFixed(4).replace(/0+$/, '').replace(/\.$/, '.0')}`
    : '$0';
  const tokFmt = (n: number) =>
    n < 1000 ? String(n)
    : n < 1_000_000 ? `${(n / 1000).toFixed(n < 10_000 ? 1 : 0)}k`
    : `${(n / 1_000_000).toFixed(1)}M`;
  return (
    <div className="miro-cost-strip">
      <span className="miro-cost-pill"><b>{fmt(t.costUsd)}</b></span>
      <span className="miro-cost-mono">{t.model}</span>
      <span className="miro-cost-mono">{tokFmt(t.inputTokens)}→{tokFmt(t.outputTokens)}</span>
      {t.inputTokens > 0 && (
        <span className="miro-cost-mono" title="Сколько токенов прилетело из кэша (90% дешевле)">
          кэш {cachePct}%
        </span>
      )}
      {t.durationMs > 0 && (
        <span className="miro-cost-mono">{(t.durationMs / 1000).toFixed(1)}s</span>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════
//  Elements section — manage custom Miro shapes/icons catalog
//
//  V2 architect uses style.ts categories, but the elements
//  table powers the legacy pipeline + future per-element
//  customisation (custom icon, label, default size).
// ═══════════════════════════════════════════════════════════

interface MiroElement {
  id: string;
  name: string;
  category: string;
  shape: string;
  icon?: string;
  description?: string;
  defaultWidth?: number;
  defaultHeight?: number;
  style?: Record<string, string>;
  /** TRUE — pulled off an imported Miro board by the system,
   *  not hand-defined. Badged in the table so the operator knows
   *  which entries to review / rename / recategorise. */
  autoExtracted?: boolean;
}

// Tiny SVG renderer for a Miro shape — same shape set that the
// backend draws on the board. Used in the elements table and the
// plan preview so the operator sees the actual shape (cloud, can,
// right_brace, …) instead of just reading the shape-type string
// and trying to imagine what it'll look like.
//
// Fill / stroke come from the element's imported style so a
// cloud extracted from a white-on-thin-grey LATO board renders
// as a white cloud with a thin grey outline — visually matching
// what'll land on the next generated Miro board.
const ShapeThumbnail: React.FC<{
  shape: string;
  fillColor?: string;
  borderColor?: string;
  size?: number;          // width in px; height auto = 0.6 * size
}> = ({ shape, fillColor, borderColor, size = 38 }) => {
  const w = size;
  const h = Math.round(size * 0.6);
  const fill = fillColor || '#ffffff';
  const stroke = borderColor || '#1f2440';
  const sw = 1.4;
  const cx = w / 2;
  const cy = h / 2;

  let body: React.ReactNode;
  switch ((shape || '').toLowerCase()) {
    case 'rectangle':
      body = <rect x={2} y={2} width={w - 4} height={h - 4} fill={fill} stroke={stroke} strokeWidth={sw} />;
      break;
    case 'round_rectangle':
      body = <rect x={2} y={2} width={w - 4} height={h - 4} rx={5} ry={5} fill={fill} stroke={stroke} strokeWidth={sw} />;
      break;
    case 'circle':
      body = <ellipse cx={cx} cy={cy} rx={w / 2 - 2} ry={h / 2 - 2} fill={fill} stroke={stroke} strokeWidth={sw} />;
      break;
    case 'diamond':
    case 'rhombus':
      body = <polygon points={`${cx},2 ${w - 2},${cy} ${cx},${h - 2} 2,${cy}`} fill={fill} stroke={stroke} strokeWidth={sw} />;
      break;
    case 'parallelogram':
      body = <polygon points={`${w * 0.22},2 ${w - 2},2 ${w * 0.78},${h - 2} 2,${h - 2}`} fill={fill} stroke={stroke} strokeWidth={sw} />;
      break;
    case 'triangle':
      body = <polygon points={`${cx},2 ${w - 2},${h - 2} 2,${h - 2}`} fill={fill} stroke={stroke} strokeWidth={sw} />;
      break;
    case 'trapezoid':
      body = <polygon points={`${w * 0.25},2 ${w * 0.75},2 ${w - 2},${h - 2} 2,${h - 2}`} fill={fill} stroke={stroke} strokeWidth={sw} />;
      break;
    case 'pentagon':
      body = <polygon points={`${cx},2 ${w - 2},${h * 0.4} ${w * 0.8},${h - 2} ${w * 0.2},${h - 2} 2,${h * 0.4}`} fill={fill} stroke={stroke} strokeWidth={sw} />;
      break;
    case 'hexagon':
      body = <polygon points={`${w * 0.25},2 ${w * 0.75},2 ${w - 2},${cy} ${w * 0.75},${h - 2} ${w * 0.25},${h - 2} 2,${cy}`} fill={fill} stroke={stroke} strokeWidth={sw} />;
      break;
    case 'octagon':
      body = <polygon points={`${w * 0.3},2 ${w * 0.7},2 ${w - 2},${h * 0.3} ${w - 2},${h * 0.7} ${w * 0.7},${h - 2} ${w * 0.3},${h - 2} 2,${h * 0.7} 2,${h * 0.3}`} fill={fill} stroke={stroke} strokeWidth={sw} />;
      break;
    case 'star': {
      const ro = Math.min(w, h) / 2 - 2;
      const ri = ro * 0.4;
      const pts: string[] = [];
      for (let i = 0; i < 10; i++) {
        const a = -Math.PI / 2 + (i * Math.PI) / 5;
        const r = i % 2 === 0 ? ro : ri;
        pts.push(`${(cx + r * Math.cos(a)).toFixed(1)},${(cy + r * Math.sin(a)).toFixed(1)}`);
      }
      body = <polygon points={pts.join(' ')} fill={fill} stroke={stroke} strokeWidth={sw} />;
      break;
    }
    case 'cloud':
      // Stylised cloud: three overlapping bumps on top, flat-ish
      // bottom — same silhouette Miro renders for a cloud shape.
      body = (
        <path
          d={`M ${w * 0.18} ${h - 4}
              Q 3 ${h - 4} 3 ${h * 0.62}
              Q 3 ${h * 0.4} ${w * 0.2} ${h * 0.42}
              Q ${w * 0.22} ${h * 0.12} ${w * 0.45} ${h * 0.22}
              Q ${w * 0.55} ${h * 0.04} ${w * 0.72} ${h * 0.22}
              Q ${w * 0.95} ${h * 0.22} ${w - 3} ${h * 0.5}
              Q ${w - 3} ${h - 4} ${w * 0.82} ${h - 4} Z`}
          fill={fill}
          stroke={stroke}
          strokeWidth={sw}
          strokeLinejoin="round"
        />
      );
      break;
    case 'can': {
      const er = Math.max(3, h * 0.16);
      body = (
        <g>
          <path
            d={`M 3 ${er + 2}
                L 3 ${h - er - 2}
                A ${w / 2 - 3} ${er} 0 0 0 ${w - 3} ${h - er - 2}
                L ${w - 3} ${er + 2}`}
            fill={fill}
            stroke={stroke}
            strokeWidth={sw}
          />
          <ellipse cx={cx} cy={er + 2} rx={w / 2 - 3} ry={er} fill={fill} stroke={stroke} strokeWidth={sw} />
        </g>
      );
      break;
    }
    case 'cross':
      body = (
        <polygon
          points={`${w * 0.35},2 ${w * 0.65},2 ${w * 0.65},${h * 0.35} ${w - 2},${h * 0.35} ${w - 2},${h * 0.65} ${w * 0.65},${h * 0.65} ${w * 0.65},${h - 2} ${w * 0.35},${h - 2} ${w * 0.35},${h * 0.65} 2,${h * 0.65} 2,${h * 0.35} ${w * 0.35},${h * 0.35}`}
          fill={fill}
          stroke={stroke}
          strokeWidth={sw}
        />
      );
      break;
    case 'right_brace':
      body = (
        <path
          d={`M ${w * 0.25} 3
              Q ${w * 0.65} 3 ${w * 0.65} ${h * 0.3}
              Q ${w * 0.65} ${cy} ${w - 4} ${cy}
              Q ${w * 0.65} ${cy} ${w * 0.65} ${h * 0.7}
              Q ${w * 0.65} ${h - 3} ${w * 0.25} ${h - 3}`}
          fill="none"
          stroke={stroke}
          strokeWidth={sw + 0.3}
          strokeLinecap="round"
        />
      );
      break;
    case 'left_brace':
      body = (
        <path
          d={`M ${w * 0.75} 3
              Q ${w * 0.35} 3 ${w * 0.35} ${h * 0.3}
              Q ${w * 0.35} ${cy} 4 ${cy}
              Q ${w * 0.35} ${cy} ${w * 0.35} ${h * 0.7}
              Q ${w * 0.35} ${h - 3} ${w * 0.75} ${h - 3}`}
          fill="none"
          stroke={stroke}
          strokeWidth={sw + 0.3}
          strokeLinecap="round"
        />
      );
      break;
    case 'right_arrow':
      body = (
        <polygon
          points={`2,${h * 0.35} ${w * 0.6},${h * 0.35} ${w * 0.6},${h * 0.12} ${w - 2},${cy} ${w * 0.6},${h * 0.88} ${w * 0.6},${h * 0.65} 2,${h * 0.65}`}
          fill={fill}
          stroke={stroke}
          strokeWidth={sw}
        />
      );
      break;
    case 'left_arrow':
      body = (
        <polygon
          points={`${w - 2},${h * 0.35} ${w * 0.4},${h * 0.35} ${w * 0.4},${h * 0.12} 2,${cy} ${w * 0.4},${h * 0.88} ${w * 0.4},${h * 0.65} ${w - 2},${h * 0.65}`}
          fill={fill}
          stroke={stroke}
          strokeWidth={sw}
        />
      );
      break;
    case 'left_right_arrow':
      body = (
        <polygon
          points={`${w * 0.22},${h * 0.35} ${w * 0.22},${h * 0.12} 2,${cy} ${w * 0.22},${h * 0.88} ${w * 0.22},${h * 0.65} ${w * 0.78},${h * 0.65} ${w * 0.78},${h * 0.88} ${w - 2},${cy} ${w * 0.78},${h * 0.12} ${w * 0.78},${h * 0.35}`}
          fill={fill}
          stroke={stroke}
          strokeWidth={sw}
        />
      );
      break;
    case 'flow_chart_predefined_process':
      body = (
        <g>
          <rect x={2} y={2} width={w - 4} height={h - 4} fill={fill} stroke={stroke} strokeWidth={sw} />
          <line x1={w * 0.18} y1={2} x2={w * 0.18} y2={h - 2} stroke={stroke} strokeWidth={sw} />
          <line x1={w * 0.82} y1={2} x2={w * 0.82} y2={h - 2} stroke={stroke} strokeWidth={sw} />
        </g>
      );
      break;
    case 'wedge_round_rectangle_callout':
      body = (
        <g>
          <rect x={2} y={2} width={w - 4} height={h - 8} rx={5} fill={fill} stroke={stroke} strokeWidth={sw} />
          <polygon points={`${w * 0.3},${h - 7} ${w * 0.42},${h - 7} ${w * 0.34},${h - 1}`} fill={fill} stroke={stroke} strokeWidth={sw} />
        </g>
      );
      break;
    default:
      // Unrecognised shape — dashed outline so it's visually
      // distinguishable from real shapes (signals "renderer will
      // fall back to round_rectangle for this one").
      body = (
        <rect x={2} y={2} width={w - 4} height={h - 4} fill="none" stroke={stroke} strokeWidth={sw} strokeDasharray="3 2" />
      );
  }

  return (
    <svg
      width={w}
      height={h}
      viewBox={`0 0 ${w} ${h}`}
      style={{ display: 'inline-block', verticalAlign: 'middle' }}
      aria-label={`shape: ${shape}`}
    >
      {body}
    </svg>
  );
};

const ELEMENT_CATEGORIES = ['client', 'service', 'database', 'cache', 'queue', 'network', 'external', 'ml', 'component'];
// Full Miro-drawable shape set — basics + domain shapes
// (cloud = AI, can = database cylinder, braces = criteria,
// hexagon/star/arrows for flow). Mirrors MIRO_DRAWABLE_SHAPES
// in the backend architect.
const ELEMENT_SHAPES = [
  'rectangle', 'round_rectangle', 'circle', 'diamond',
  'parallelogram', 'triangle', 'trapezoid',
  'pentagon', 'hexagon', 'octagon', 'star',
  'cloud', 'can', 'cross',
  'left_brace', 'right_brace',
  'right_arrow', 'left_arrow', 'left_right_arrow',
];

const ElementsSection: React.FC = () => {
  const [elements, setElements] = useState<MiroElement[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<MiroElement | null>(null);
  const [adding, setAdding] = useState(false);
  // Bulk-clear progress — number of auto-extracted rows still in
  // flight. Disables the button + shows a count while the loop runs.
  const [clearing, setClearing] = useState(0);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/miro/elements`);
      if (r.ok) {
        const data = await r.json();
        if (Array.isArray(data)) setElements(data);
      }
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const remove = async (id: string) => {
    if (!window.confirm('Удалить элемент?')) return;
    await fetch(`${API}/api/miro/elements/${id}`, { method: 'DELETE' });
    await reload();
  };

  // Bulk-delete every auto-extracted element. Use this after the
  // backend's import logic improves (sampleText naming, white-shape
  // capture, etc.) — wipes the stale entries so a re-import lands
  // a clean catalog. Hand-curated rows are left alone.
  //
  // No new backend endpoint: loops the existing DELETE one row at
  // a time. Catalog is ≤ a few dozen rows in practice, so the
  // round-trip cost is negligible.
  const autoExtracted = elements.filter((e) => e.autoExtracted);
  const clearAutoExtracted = async () => {
    if (autoExtracted.length === 0) return;
    if (!window.confirm(
      `Удалить все авто-извлечённые элементы (${autoExtracted.length})?\n\n`
      + `Останутся только ручные. После этого переимпортируй доски — `
      + `новый бэкенд подцепит белые шейпы и даст осмысленные имена.`,
    )) return;
    setClearing(autoExtracted.length);
    try {
      // Sequential, not parallel — keeps server pressure low and
      // makes the "N left" counter meaningful in the UI.
      for (let i = 0; i < autoExtracted.length; i++) {
        await fetch(`${API}/api/miro/elements/${autoExtracted[i].id}`, { method: 'DELETE' })
          .catch(() => {}); // swallow individual failures, keep going
        setClearing(autoExtracted.length - i - 1);
      }
    } finally {
      setClearing(0);
      await reload();
    }
  };

  return (
    <div className="miro-section">
      <div className="miro-section-head">
        <h3>Кастомные элементы · {elements.length}{autoExtracted.length > 0 && <span style={{ color: '#7a7590', fontWeight: 'normal', marginLeft: 8 }}>({autoExtracted.length} из Miro)</span>}</h3>
        <div style={{ display: 'flex', gap: 8 }}>
          {autoExtracted.length > 0 && (
            <button
              className="btn-secondary"
              onClick={clearAutoExtracted}
              disabled={clearing > 0}
              title="Удалить все авто-извлечённые элементы — потом переимпортируй доски, чтобы пересобрать каталог в стиле LATO"
            >
              {clearing > 0 ? `Удаляю… ${clearing} осталось` : `Очистить авто (${autoExtracted.length})`}
            </button>
          )}
          <button
            className="btn-secondary"
            onClick={() => { setEditing({ id: '', name: '', category: 'service', shape: 'round_rectangle', icon: '', description: '', defaultWidth: 240, defaultHeight: 90 }); setAdding(true); }}
          >
            + Добавить
          </button>
        </div>
      </div>

      {loading && <div className="miro-empty">Загружаю…</div>}
      {!loading && elements.length === 0 && (
        <div className="miro-empty">
          Нет кастомных элементов. V2 Architect работает на встроенной палитре —
          добавляй сюда только если нужны иконки/формы за пределами стандартного набора.
        </div>
      )}
      {!loading && elements.length > 0 && (
        <table className="miro-elements-table">
          <thead>
            <tr>
              <th>Превью</th>
              <th>Имя</th>
              <th>Категория</th>
              <th>Форма</th>
              <th>Иконка</th>
              <th>Размер</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {elements.map((el) => (
              <tr key={el.id}>
                <td style={{ width: 50, textAlign: 'center' }}>
                  <ShapeThumbnail
                    shape={el.shape}
                    fillColor={el.style?.fillColor}
                    borderColor={el.style?.borderColor}
                  />
                </td>
                <td className="miro-el-name">
                  {el.name}
                  {el.autoExtracted && (
                    <span className="miro-el-auto-badge" title="Авто-извлечён из импортированной Miro-доски — переименуй / проверь категорию">
                      авто из Miro
                    </span>
                  )}
                </td>
                <td><span className={`miro-cat-pill miro-cat-${el.category}`}>{el.category}</span></td>
                <td><code>{el.shape}</code></td>
                <td>{el.icon || '—'}</td>
                <td>{el.defaultWidth || 240}×{el.defaultHeight || 90}</td>
                <td>
                  <button className="miro-el-edit" onClick={() => { setEditing(el); setAdding(false); }}>edit</button>
                  <button className="miro-el-remove" onClick={() => remove(el.id)}>×</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {editing && (
        <ElementEditor
          element={editing}
          isNew={adding}
          onCancel={() => { setEditing(null); setAdding(false); }}
          onSaved={async () => { setEditing(null); setAdding(false); await reload(); }}
        />
      )}
    </div>
  );
};

const ElementEditor: React.FC<{
  element: MiroElement;
  isNew: boolean;
  onCancel: () => void;
  onSaved: () => Promise<void> | void;
}> = ({ element, isNew, onCancel, onSaved }) => {
  const [name, setName] = useState(element.name);
  const [category, setCategory] = useState(element.category);
  const [shape, setShape] = useState(element.shape);
  const [icon, setIcon] = useState(element.icon || '');
  const [description, setDescription] = useState(element.description || '');
  const [width, setWidth] = useState(String(element.defaultWidth || 240));
  const [height, setHeight] = useState(String(element.defaultHeight || 90));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const save = async () => {
    if (!name.trim()) return;
    setSaving(true);
    setErr(null);
    try {
      const body = {
        name: name.trim(),
        category,
        shape,
        icon: icon.trim() || undefined,
        description: description.trim() || '',
        defaultWidth: Number(width) || 240,
        defaultHeight: Number(height) || 90,
        style: element.style || {},
      };
      const r = await fetch(`${API}/api/miro/elements`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        setErr(d.error || `HTTP ${r.status}`);
        return;
      }
      await onSaved();
    } finally { setSaving(false); }
  };

  return (
    <div className="miro-el-editor">
      <div className="miro-el-editor-head">{isNew ? 'Новый элемент' : `Редактировать "${element.name}"`}</div>
      <div className="miro-el-editor-row">
        <label>Имя</label>
        <input className="form-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Backend Service" />
      </div>
      <div className="miro-el-editor-row">
        <label>Категория</label>
        <select className="form-select" value={category} onChange={(e) => setCategory(e.target.value)}>
          {ELEMENT_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      <div className="miro-el-editor-row">
        <label>Форма</label>
        <select className="form-select" value={shape} onChange={(e) => setShape(e.target.value)}>
          {ELEMENT_SHAPES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      <div className="miro-el-editor-row">
        <label>Иконка (emoji / unicode)</label>
        <input className="form-input" value={icon} onChange={(e) => setIcon(e.target.value)} placeholder="🛢️" maxLength={4} />
      </div>
      <div className="miro-el-editor-row">
        <label>Описание</label>
        <input className="form-input" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="REST API + business logic" />
      </div>
      <div className="miro-el-editor-row miro-el-editor-row-pair">
        <div>
          <label>Ширина</label>
          <input className="form-input" type="number" value={width} onChange={(e) => setWidth(e.target.value)} />
        </div>
        <div>
          <label>Высота</label>
          <input className="form-input" type="number" value={height} onChange={(e) => setHeight(e.target.value)} />
        </div>
      </div>
      {err && <div className="form-result bad" style={{ marginTop: 10 }}>✗ {err}</div>}
      <div className="miro-el-editor-actions">
        <button className="btn-secondary" onClick={onCancel} disabled={saving}>Отмена</button>
        <button className="btn-primary" onClick={save} disabled={saving || !name.trim()}>
          {saving ? 'Сохраняю…' : 'Сохранить'}
        </button>
      </div>
    </div>
  );
};

// Visual preview of the plan — components grouped by layer + a
// connections list. No canvas drawing; the goal is "did the AI
// pick the right shape?", not pixel-perfect rendering.
const PlanPreviewView: React.FC<{ plan: PlanJson }> = ({ plan }) => {
  // Group by layer in emit order.
  const layers: Array<{ name: string; comps: PlanComponent[] }> = [];
  for (const c of plan.components) {
    let layer = layers.find((l) => l.name === c.layer);
    if (!layer) { layer = { name: c.layer || 'Main', comps: [] }; layers.push(layer); }
    layer.comps.push(c);
  }

  // Cases — group layers by "Case N — …" prefix so the preview
  // mirrors the colored case frames the Miro renderer draws on the
  // board. Layers without a Case prefix go into a default bucket.
  // The colour cycle matches architect.ts (purple/green/pink/blue/teal).
  const CASE_COLORS = ['#963ac7', '#0ea54c', '#e32383', '#0b5abc', '#2da6b0', '#d4a574'];
  const CASE_RE = /^Case\s+(\S+?)\s*[—-]\s*(.+)$/i;
  const caseGroups: Array<{ number: string; title: string; description?: string; color: string; layers: typeof layers }> = [];
  const noCase: typeof layers = [];
  const caseMeta = new Map<string, { title: string; description?: string }>();
  for (const c of plan.cases || []) {
    caseMeta.set(c.number, { title: c.title, description: c.description });
  }
  for (const layer of layers) {
    const m = layer.name.match(CASE_RE);
    if (!m) { noCase.push(layer); continue; }
    const num = m[1].trim();
    let grp = caseGroups.find((g) => g.number === num);
    if (!grp) {
      const meta = caseMeta.get(num);
      grp = {
        number: num,
        title: meta?.title || m[2].trim(),
        description: meta?.description,
        color: CASE_COLORS[(caseGroups.length) % CASE_COLORS.length],
        layers: [],
      };
      caseGroups.push(grp);
    }
    grp.layers.push(layer);
  }

  // Group annotations by their target so each component card can
  // show its API/note side-tags inline ("API: Twilio (For SMS)").
  const annotationsByTarget = new Map<string, PlanAnnotation[]>();
  for (const a of plan.annotations || []) {
    const list = annotationsByTarget.get(a.attachTo) || [];
    list.push(a);
    annotationsByTarget.set(a.attachTo, list);
  }

  // Render one layer as a visual section header + its components.
  // Each component is a three-column row mirroring the Miro board
  // exactly: API/note annotations (left) — step box (centre, narrow,
  // optional LATO AI cloud border) — criteria text (right, wide).
  // This makes the preview a one-to-one mock of what materialize
  // will draw, not just a structured list.
  const renderLayer = (l: { name: string; comps: PlanComponent[] }) => (
    <div key={l.name} style={{ marginBottom: 14 }}>
      <div style={{
        fontSize: 11, fontWeight: 600, color: '#7a7590',
        textTransform: 'uppercase', letterSpacing: 0.6,
        marginBottom: 6, marginTop: 4,
      }}>{l.name}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {l.comps.map((c) => {
          const anns = annotationsByTarget.get(c.name) || [];
          const cleanName = (c.name || '').replace(/^\[\S+?\]\s*/, '');
          // LATO AI cloud border for AI components (shape=cloud).
          const isLatoAi = c.shape === 'cloud';
          const boxBorder = isLatoAi ? '#4f8bae' : '#c5cad6';
          const boxBorderWidth = isLatoAi ? 2 : 1;
          return (
            <div key={c.name} style={{
              display: 'grid',
              // [ annotations | brace | step-box | criteria ]
              gridTemplateColumns: '170px 1fr 1fr',
              gap: 12,
              alignItems: 'flex-start',
            }}>
              {/* LEFT: API / note annotations stacked vertically */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {anns.map((a, i) => {
                  const parts = (a.label || '').split(':');
                  const lhs = parts[0];
                  const rhs = parts.slice(1).join(':').trim();
                  return (
                    <div key={i} style={{
                      fontSize: 10,
                      padding: '4px 6px',
                      borderRadius: 3,
                      border: a.kind === 'note'
                        ? '1px dashed #7a7590'
                        : '1px solid #c5cad6',
                      background: '#ffffff',
                      color: '#1f2440',
                      lineHeight: 1.3,
                    }}>
                      <div style={{ fontWeight: 600 }}>{lhs}:</div>
                      {rhs && <div style={{ fontStyle: 'italic', color: '#7a7590' }}>{rhs}</div>}
                    </div>
                  );
                })}
              </div>

              {/* CENTRE: narrow step box mock (matches LATO 306×130) */}
              <div style={{
                padding: '8px 10px',
                background: '#ffffff',
                color: '#1f2440',
                border: `${boxBorderWidth}px solid ${boxBorder}`,
                borderRadius: 4,
                minHeight: 60,
                position: 'relative',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2, flexWrap: 'wrap' }}>
                  {c.step && (
                    <span style={{
                      fontSize: 10, fontWeight: 700,
                      padding: '1px 5px', borderRadius: 2,
                      background: '#f4f0ea',
                      color: '#b88758',
                      fontFamily: 'ui-monospace, monospace',
                    }}>{c.step}</span>
                  )}
                  {c.shape && <ShapeThumbnail shape={c.shape} size={28} />}
                </div>
                <div style={{ fontSize: 12, fontWeight: 600 }}>{cleanName}</div>
                {c.description && (
                  <div style={{ fontSize: 10, color: '#7a7590', fontStyle: 'italic', marginTop: 2 }}>
                    {c.description}
                  </div>
                )}
              </div>

              {/* RIGHT: criteria bullets (wide column, mocks the LATO
                  `}` brace + paragraph block). Uses `border-left` to
                  fake the brace look without rendering an SVG. */}
              <div style={{
                fontSize: 11, lineHeight: 1.45, color: '#1f2440',
                paddingLeft: 14,
                borderLeft: c.criteria && c.criteria.length ? '2px solid #c5cad6' : 'none',
              }}>
                {c.criteria && c.criteria.length > 0 ? (
                  <ul style={{ margin: 0, paddingLeft: 14 }}>
                    {c.criteria.map((b, i) => <li key={i} style={{ marginBottom: 3 }}>{b}</li>)}
                  </ul>
                ) : (
                  <span style={{ color: '#7a7590', fontSize: 10 }}>—</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="miro-plan">
      {/* Phase header — mirrors the navy header band the Miro template
          will render at the top of the duplicated workflow board. */}
      {(plan.phaseTitle || plan.phaseDescription) && (
        <div style={{
          background: '#1a2445', color: '#ffffff',
          padding: '16px 20px', borderRadius: 4,
          marginBottom: 16,
        }}>
          {plan.phaseTitle && (
            <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 6 }}>{plan.phaseTitle}</div>
          )}
          {plan.phaseDescription && (
            <div style={{ fontSize: 11, lineHeight: 1.45, opacity: 0.9 }}>{plan.phaseDescription}</div>
          )}
        </div>
      )}

      {caseGroups.length > 0 && caseGroups.map((grp) => (
        // Each case = white workspace card with a coloured border,
        // mirroring Dana's per-case frame on the actual Miro board.
        <div key={grp.number} style={{
          marginBottom: 24, padding: 16,
          border: `2px solid ${grp.color}`, borderRadius: 6,
          background: '#ffffff',
          color: '#1f2440',
        }}>
          <div style={{
            fontSize: 14, fontWeight: 700, color: grp.color,
            marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.6,
          }}>
            CASE {grp.number}: {grp.title}
          </div>
          {grp.description && (
            <div style={{ fontSize: 11, color: '#5b6275', marginBottom: 14, lineHeight: 1.45 }}>
              {grp.description}
            </div>
          )}
          {grp.layers.map(renderLayer)}
        </div>
      ))}
      {noCase.length > 0 && (
        // Cross-cutting layers (Centralized Dashboard etc.) — neutral
        // white card without a coloured border so the visual
        // hierarchy reads "this sits outside every case".
        <div style={{
          marginBottom: 24, padding: 16,
          border: '2px solid #c5cad6', borderRadius: 6,
          background: '#ffffff', color: '#1f2440',
        }}>
          <div style={{
            fontSize: 12, fontWeight: 700, color: '#5b6275',
            marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.6,
          }}>
            OPERATIONAL CORE
          </div>
          {noCase.map(renderLayer)}
        </div>
      )}

      {plan.connectors.length > 0 && (
        <div className="miro-plan-connectors">
          <div className="miro-plan-connectors-head">Связи</div>
          {plan.connectors.map((c, i) => (
            <div key={i} className={`miro-plan-conn miro-conn-${c.kind || 'solid'}`}>
              <span className="miro-plan-conn-from">{c.from}</span>
              <span className="miro-plan-conn-arrow">{c.kind === 'dashed' ? '⇢' : c.kind === 'dotted' ? '⤍' : '→'}</span>
              <span className="miro-plan-conn-to">{c.to}</span>
              {c.label && <span className="miro-plan-conn-label">{c.label}</span>}
              {c.branch === 'side' && (
                <span style={{
                  fontSize: 9, marginLeft: 6,
                  padding: '1px 4px', borderRadius: 2,
                  background: 'rgba(122,117,144,0.2)', color: '#7a7590',
                }}>side</span>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Key Capabilities block — LATO methodology section 11.3. */}
      {plan.keyCapabilities && plan.keyCapabilities.length > 0 && (
        <div style={{
          marginTop: 16,
          padding: 12,
          border: '1px solid #5d7c93',
          borderRadius: 4,
          background: 'rgba(137,167,192,0.06)',
        }}>
          <div style={{
            fontSize: 12, fontWeight: 600,
            color: '#89a7c0', marginBottom: 8,
            textTransform: 'uppercase', letterSpacing: 0.5,
          }}>
            Centralized Dashboard — Key Capabilities
          </div>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, lineHeight: 1.5, color: '#d4cce5' }}>
            {plan.keyCapabilities.map((b, i) => <li key={i}>{b}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════
//  AudioDropzone — drag/drop or pick a file → Whisper → optional
//  Claude-based compression → set as project description.
//
//  The whole flow is opt-in: you either type the description by
//  hand OR drop an audio file. After transcription you see the
//  raw text and can either: (a) "Use as is", (b) "Compress
//  through Claude" for long meetings, (c) cancel and start over.
// ═══════════════════════════════════════════════════════════

interface TranscriptResponse {
  id: string | null;
  transcript: string;
  durationSec: number | null;
  language: string | null;
  costUsd: number;
  whisperMs: number;
  summary?: string | null;
  /** 'audio' — went through Whisper (has a DB id, can be compressed).
   *  'text'  — a .txt/.md file read straight in the browser; no
   *  Whisper, no cost, no DB row. */
  source?: 'audio' | 'text';
}

const AudioDropzone: React.FC<{ onSummary: (text: string) => void }> = ({ onSummary }) => {
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState<string | null>(null);
  const [transcript, setTranscript] = useState<TranscriptResponse | null>(null);
  const [editing, setEditing] = useState('');
  const [compressing, setCompressing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFile = async (file: File) => {
    const isText = file.type === 'text/plain' || /\.(txt|md|markdown)$/i.test(file.name);
    const isAudio = file.type.startsWith('audio/') || /\.(mp3|m4a|wav|webm|ogg)$/i.test(file.name);

    if (!isText && !isAudio) {
      setError(`Не похоже ни на аудио, ни на текст: ${file.name}`);
      return;
    }

    // .txt / .md — already text. Read it in the browser, no Whisper,
    // no upload, no cost. The user gets it straight in the editable
    // box. id stays null so the compress button (which needs a DB
    // row) won't show — for a text file the user can just trim it
    // by hand if it's huge.
    if (isText) {
      if (file.size > 5 * 1024 * 1024) {
        setError(`Текстовый файл ${(file.size / 1024 / 1024).toFixed(1)}MB слишком большой (макс 5MB)`);
        return;
      }
      setError(null);
      try {
        const text = (await file.text()).trim();
        if (!text) { setError('Файл пустой'); return; }
        setTranscript({
          id: null,
          transcript: text,
          durationSec: null,
          language: 'text',
          costUsd: 0,
          whisperMs: 0,
          source: 'text',
        });
        setEditing(text);
        setProgress(null);
      } catch (err: any) {
        setError(err.message || 'Не удалось прочитать файл');
      }
      return;
    }

    // Audio path — through Whisper.
    if (file.size > 50 * 1024 * 1024) {
      setError(`Файл ${(file.size / 1024 / 1024).toFixed(1)}MB слишком большой (макс 50MB)`);
      return;
    }
    setError(null);
    setProgress('Транскрибирую…');
    setUploading(true);

    const fd = new FormData();
    fd.append('file', file);
    fd.append('language', 'en');
    try {
      const r = await fetch(`${API}/api/audio/transcribe`, { method: 'POST', body: fd });
      const data = await r.json();
      if (!r.ok) {
        const map: Record<string, string> = {
          whisper_not_configured: 'OPENAI_API_KEY не задан на сервере. Положи в Vault.',
          file_too_large: 'Файл больше 25MB — Whisper не примет. Сожми / разрежь.',
          unsupported_format: 'Этот формат файла не поддерживается.',
        };
        setError(map[data.error] || data.message || data.error || `HTTP ${r.status}`);
        return;
      }
      const t = data.transcript || data;
      setTranscript({ ...t, source: 'audio' });
      setEditing(t.transcript || '');
      setProgress(null);
    } catch (err: any) {
      setError(err.message || 'Network error');
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  };

  const applyAsDescription = (text: string) => {
    onSummary(text);
    // Reset state after handing off — user goes back to "drop another"
    setTranscript(null);
    setEditing('');
  };

  const compressNow = async () => {
    if (!transcript || !transcript.id) return;
    setCompressing(true);
    setProgress('Сжимаю через Claude (chunked)…');
    setError(null);
    try {
      const r = await fetch(`${API}/api/audio/${transcript.id}/compress`, { method: 'POST' });
      const data = await r.json();
      if (!r.ok) {
        setError(data.message || data.error || 'compress_failed');
        return;
      }
      const updated = data.transcript;
      setTranscript({ ...transcript, summary: updated.summary });
      setEditing(updated.summary || editing);
      setProgress(null);
    } catch (err: any) {
      setError(err.message || 'Network error');
    } finally {
      setCompressing(false);
    }
  };

  // Word counter — switches the "compress" CTA visibility.
  const wordCount = editing.trim().split(/\s+/).filter(Boolean).length;
  const isLong = wordCount > 5000;

  return (
    <div className="audio-dropzone-wrap" style={{ marginTop: 8 }}>
      {!transcript && (
        <label
          className={`audio-dropzone ${dragOver ? 'audio-dropzone-active' : ''} ${uploading ? 'audio-dropzone-busy' : ''}`}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
        >
          <input type="file" accept="audio/*,.mp3,.m4a,.wav,.webm,.ogg,.txt,.md,.markdown,text/plain" hidden onChange={handleFileInput} disabled={uploading} />
          <span className="audio-dropzone-icon">{uploading ? '⏳' : '🎙'}</span>
          <span className="audio-dropzone-text">
            {uploading ? (progress || 'Грузу…') : 'Перетащи .mp3 / .m4a / .wav / .txt сюда — или клик чтобы выбрать'}
          </span>
          {!uploading && (
            <span className="audio-dropzone-hint">
              Аудио → Whisper транскрибирует · .txt — берётся как есть → Mira рисует
            </span>
          )}
        </label>
      )}

      {error && <div className="form-result bad" style={{ marginTop: 8 }}>✗ {error}</div>}

      {transcript && (
        <div className="audio-result">
          <div className="audio-result-head">
            <div>
              <div className="audio-result-title">
                {transcript.source === 'text'
                  ? (transcript.summary ? '✓ Текст из файла + Summary' : '✓ Текст из файла')
                  : (transcript.summary ? '✓ Transcript + Summary' : '✓ Transcript')}
              </div>
              <div className="audio-result-meta">
                {transcript.source === 'text'
                  ? `txt · ${wordCount} слов`
                  : <>
                      {transcript.durationSec ? `${Math.round(transcript.durationSec / 60)} мин` : '—'} ·
                      {' '}{transcript.language || 'auto'} ·
                      {' '}${transcript.costUsd.toFixed(4)} ·
                      {' '}{wordCount} слов
                    </>}
              </div>
            </div>
            <button className="btn-secondary" onClick={() => { setTranscript(null); setEditing(''); }}>
              ✕ Сбросить
            </button>
          </div>

          {progress && <div className="audio-progress">{progress}</div>}

          <textarea
            className="form-textarea"
            rows={8}
            value={editing}
            onChange={(e) => setEditing(e.target.value)}
            style={{ marginTop: 10 }}
          />

          <div className="audio-result-actions">
            {/* Compress needs a DB row (transcript.id) — only audio
                transcripts have one. For a dropped .txt the user
                trims by hand if it's huge. */}
            {isLong && !transcript.summary && transcript.id && (
              <button
                className="btn-secondary"
                onClick={compressNow}
                disabled={compressing}
                title="Длинный транскрипт — Claude сожмёт в design brief"
              >
                {compressing ? 'Сжимаю…' : `Сжать через Claude (${wordCount} слов)`}
              </button>
            )}
            <button
              className="btn-primary"
              onClick={() => applyAsDescription(editing)}
              disabled={editing.trim().length < 20}
            >
              Использовать как описание →
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
